import Head from 'next/head'
import Script from 'next/script'
import { FaExclamationTriangle, FaUserTie, FaBan, FaHandshake, FaDollarSign } from 'react-icons/fa'
import { LegalPageLayout, LegalSection, LegalHighlight } from '../../components/index/LegalPageLayout'

export default function Obligations() {
  return (
    <>
      <Head>
        <title>Terms on Direct Marketing, Financial Transaction & Brand Representation</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no"/>
        <meta name="description" content="These terms outline some of the strict guidelines for service providers on the StringsSG platform."/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:url" content="https://www.stringssg.com/terms/obligations" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Terms on Direct Marketing, Financial Transaction & Brand Representation"/>
        <meta property="og:description" content="These terms outline some of the strict guidelines for service providers on the StringsSG platform." />
        <meta property="og:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/Google-icon.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/Google-icon.png" />
        <meta name="twitter:site" content="@stringssg"/> 
        <meta name="twitter:url" content="https://www.stringssg.com/terms/obligations"/>
        <meta name="twitter:title" content="Terms on Direct Marketing, Financial Transaction & Brand Representation"/> 
        <meta name="twitter:description" content="These terms outline some of the strict guidelines for service providers on the StringsSG platform."/>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href="https://www.stringssg.com/terms/obligations"/>
      </Head>
      
      <LegalPageLayout
        title="Service Provider Obligations"
        subtitle="Terms on Direct Marketing, Financial Transaction & Brand Representation"
        icon={FaUserTie}
        iconBgColor="from-red-500 to-rose-600"
        heroBgColor="from-slate-900 via-slate-800 to-red-900"
      >
        <LegalHighlight color="red">
          <div className="flex items-start gap-3">
            <FaExclamationTriangle className="text-red-600 text-xl mt-1 shrink-0" />
            <p className="font-semibold">
              Violation of any of these terms will result in a breach of contract, immediate ban (temporary or permanent) from the StringsSG platform, and may incur penalties of up 
              to $20,000, which is to be paid by the service provider to StringsSG.
            </p>
          </div>
        </LegalHighlight>

        <LegalSection title="When is it applicable?">
          <p className="mb-3">If you are a service provider on the StringsSG platform in any of the following job-based models:</p>
          <div className="bg-slate-50 rounded-lg p-4">
            <ul className="space-y-2">
              <li className="flex items-center gap-2"><span className="text-orange-500">•</span> A/C Service Provider</li>
              <li className="flex items-center gap-2"><span className="text-orange-500">•</span> Handyman Service Provider</li>
              <li className="flex items-center gap-2"><span className="text-orange-500">•</span> Part-Time Maid Service Provider</li>
            </ul>
          </div>
        </LegalSection>

        <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <FaBan className="text-red-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">1. Non-Solicitation Clause</h2>
          </div>
          <div className="text-slate-600">
            <p className="font-semibold mb-2">Direct Marketing Prohibition:</p>
            <p>
              Service providers are strictly prohibited from engaging in any form of direct marketing while serving a StringsSG client. This includes, but is not 
              limited to, distributing business cards, providing personal contact information, issuing receipts or invoices, or affixing any promotional material or 
              stickers on the client&apos;s residence or household items.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <FaDollarSign className="text-amber-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">2. Financial Transaction Restrictions</h2>
          </div>
          <div className="text-slate-600">
            <p className="font-semibold mb-2">Prohibition of Direct Financial Transactions:</p>
            <p>
              Service providers must not conduct any financial transactions directly with the client without the explicit knowledge and consent of StringsSG. All financial 
              transactions must be processed through the platform to ensure transparency and accountability.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FaHandshake className="text-blue-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">3. Brand Representation and Reputation</h2>
          </div>
          <div className="text-slate-600">
            <p className="font-semibold mb-2">Brand Representation Obligation:</p>
            <p>
              Service providers are representatives of StringsSG while serving its clients. They are expected to uphold the brand&apos;s reputation and refrain from engaging in 
              any communication or activity that may defame or negatively impact the StringsSG brand image while providing services to clients.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <FaExclamationTriangle className="text-purple-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">4. Platform Misuse Prohibition</h2>
          </div>
          <div className="text-slate-600">
            <p className="font-semibold mb-2">Prohibition of Misuse for Financial, Marketing, or Sales Intentions:</p>
            <p>
              Service providers are strictly prohibited from misusing the StringsSG platform for any financial gain, marketing purposes, or sales intentions outside the 
              scope of providing services to StringsSG clients. This includes, but is not limited to, using platform features or client interactions for personal financial gain, 
              marketing one&apos;s services to clients outside the platform, or utilizing client data for marketing or sales activities unrelated to StringsSG services.
            </p>
          </div>
        </div>
      </LegalPageLayout>

      <Script 
        id="google-tagmanager-new"
        src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
        strategy="lazyOnload"
        onLoad={() => {              
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'G-QD1HGGHDYJ');
        }} 
      />
      <Script id="facebook-tracking" strategy="lazyOnload">
        {`!function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '128634041116395');
          fbq('track', 'PageView');`}
      </Script>
    </>
  )
}
